import {
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  type Observable,
  share,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from "rxjs"
import type { SpineItem } from "../.."
import { isDefined } from "../../utils/isDefined"
import type { EnhancerOutput, RootEnhancer } from "../types/enhancer"
import { createOrderedRangeFromSelection } from "./selection"
import { trackSpineItemSelection } from "./trackSpineItemSelection"

type SelectionChange = {
  itemIndex: number
  type: "change"
  selection: Selection
}

type SelectionOver = {
  itemIndex: number
  type: "over"
  event: Event
  selection: Selection
}

type SelectionValue = SelectionChange | SelectionOver | undefined

export const selectionEnhancer =
  <InheritOptions, InheritOutput extends EnhancerOutput<RootEnhancer>>(
    next: (options: InheritOptions) => InheritOutput,
  ) =>
  (
    options: InheritOptions,
  ): InheritOutput & {
    selection: {
      selection$: Observable<SelectionValue>
      selectionStart$: Observable<boolean>
      selectionEnd$: Observable<void>
      selectionOver$: Observable<SelectionOver>
      lastSelectionOnPointerdown$: Observable<SelectionValue>
      getSelection: () => SelectionValue
      createOrderedRangeFromSelection: (params: {
        selection: {
          anchorNode?: Node | null
          anchorOffset?: number
          focusNode?: Node | null
          focusOffset?: number
        }
        spineItem: SpineItem
      }) => Range | undefined
    }
  } => {
    const reader = next(options)
    let lasSelection: SelectionValue

    const trackedSelection$ = merge(
      ...reader.spineItemsManager.items.map((spineItem) => {
        const itemIndex =
          reader.spineItemsManager.getSpineItemIndex(spineItem) ?? 0

        return trackSpineItemSelection(spineItem).pipe(
          map((entry) => {
            if (!entry) return undefined

            return {
              ...entry,
              itemIndex,
            }
          }),
        )
      }),
    ).pipe(
      startWith(undefined),
      distinctUntilChanged(),
      tap((value) => {
        lasSelection = value
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    )

    const selection$ = trackedSelection$

    const selectionStart$ = trackedSelection$.pipe(
      map((selection) => !!selection),
      distinctUntilChanged(),
      filter((isSelecting) => isSelecting),
      share(),
    )

    const selectionEnd$ = selectionStart$.pipe(
      switchMap(() => selection$),
      distinctUntilChanged(),
      filter((selection) => !selection),
      share(),
    )

    const selectionOver$ = trackedSelection$.pipe(
      filter((selection) => selection?.type === "over"),
      share(),
    )

    const lastSelectionOnPointerdown$ = reader.context
      .watch(`rootElement`)
      .pipe(
        filter(isDefined),
        switchMap((container) => fromEvent(container, "pointerdown")),
        withLatestFrom(selection$),
        map(([, selection]) => selection),
        startWith(undefined),
        shareReplay({ refCount: true, bufferSize: 1 }),
      )

    merge(selection$, lastSelectionOnPointerdown$)
      .pipe(takeUntil(reader.$.destroy$))
      .subscribe()

    return {
      ...reader,
      selection: {
        selection$,
        selectionStart$,
        selectionEnd$,
        selectionOver$,
        lastSelectionOnPointerdown$,
        getSelection: () => lasSelection,
        createOrderedRangeFromSelection,
      },
    }
  }
